'use client'

// src/useClickOutside.ts
import { useCallback, useEffect } from "react";
var useClickOutside = (handler, ref) => {
  const listener = useCallback(
    (event) => {
      if (!ref.current || !event.target || !(event.target instanceof Node) || ref.current.contains(event.target)) {
        return;
      }
      handler();
    },
    [ref, handler]
  );
  useEffect(() => {
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [listener]);
};
var useClickOutside_default = useClickOutside;

// src/useDebounce.ts
import { useState, useEffect as useEffect2 } from "react";
var useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect2(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
};
var useDebounce_default = useDebounce;

// src/useScrollSuggestionIntoView.ts
import { useEffect as useEffect3 } from "react";
var useScrollSuggestionIntoView = (focusedIndex, ref) => {
  const scrollOffset = 8;
  useEffect3(() => {
    if (focusedIndex < 0 || !ref.current) return;
    const container = ref.current;
    const suggestionItems = container.querySelectorAll("button");
    if (focusedIndex >= suggestionItems.length) return;
    const focusedItem = suggestionItems[focusedIndex];
    if (!focusedItem) return;
    const containerRect = container.getBoundingClientRect();
    const itemRect = focusedItem.getBoundingClientRect();
    if (itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom) {
      return;
    }
    if (itemRect.bottom > containerRect.bottom) {
      container.scrollTop += itemRect.bottom - containerRect.bottom + scrollOffset;
    } else {
      container.scrollTop -= containerRect.top - itemRect.top + scrollOffset;
    }
  }, [focusedIndex, ref]);
};
var useScrollSuggestionIntoView_default = useScrollSuggestionIntoView;

// src/useBreakpoint.ts
import { useState as useState2, useEffect as useEffect4, useCallback as useCallback2 } from "react";
import { breakpointValues } from "@shared/constants";
var getBreakpointValues = (width) => ({
  isMobile: width < breakpointValues.xs,
  isLargeMobile: width >= breakpointValues.xs,
  isTablet: width >= breakpointValues.sm,
  isLargeTablet: width >= breakpointValues.md,
  isDesktop: width >= breakpointValues.lg,
  isLargeDesktop: width >= breakpointValues.xl,
  isExtraLarge: width >= breakpointValues["2xl"],
  isUltraLarge: width >= breakpointValues["3xl"]
});
var defaultBreakpoint = {
  isMobile: true,
  isLargeMobile: false,
  isTablet: false,
  isLargeTablet: false,
  isDesktop: false,
  isLargeDesktop: false,
  isExtraLarge: false,
  isUltraLarge: false
};
var useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState2(() => {
    if (typeof window !== "undefined") {
      return getBreakpointValues(window.innerWidth);
    }
    return defaultBreakpoint;
  });
  const handleResize = useCallback2(() => {
    setBreakpoint(getBreakpointValues(window.innerWidth));
  }, []);
  useEffect4(() => {
    let timeoutId;
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };
    window.addEventListener("resize", debouncedHandleResize);
    handleResize();
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, [handleResize]);
  return breakpoint;
};
var useBreakpoint_default = useBreakpoint;

// src/useScale.ts
import { useEffect as useEffect5, useState as useState3, useCallback as useCallback3 } from "react";
var useScale = ({
  containerRef,
  contentRef,
  dependencies = [],
  minScale = 0.3,
  maxScale = 1,
  fitToWidth = false,
  padding = 0,
  resetOnChange = false
}) => {
  const [scale, setScale] = useState3(maxScale);
  const getDimensions = useCallback3(
    (container, content) => ({
      containerWidth: container.clientWidth - padding * 2,
      containerHeight: container.clientHeight - padding * 2,
      contentWidth: content.scrollWidth,
      contentHeight: content.scrollHeight
    }),
    [padding]
  );
  const clampScale = useCallback3(
    (scale2) => Math.min(Math.max(scale2, minScale), maxScale),
    [minScale, maxScale]
  );
  const calculateScale = useCallback3(
    (container, content) => {
      const dimensions = getDimensions(container, content);
      const { containerWidth, containerHeight, contentWidth, contentHeight } = dimensions;
      if (fitToWidth) {
        const scaleX2 = containerWidth / contentWidth;
        return clampScale(scaleX2);
      }
      const scaleX = containerWidth / contentWidth;
      const scaleY = containerHeight / contentHeight;
      return clampScale(Math.min(scaleX, scaleY));
    },
    [getDimensions, clampScale, fitToWidth]
  );
  const updateScale = useCallback3(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    setScale(calculateScale(container, content));
  }, [containerRef, contentRef, calculateScale]);
  useEffect5(() => {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateScale);
    });
    const container = containerRef.current;
    const content = contentRef.current;
    if (container) resizeObserver.observe(container);
    if (content) resizeObserver.observe(content);
    window.addEventListener("resize", updateScale);
    updateScale();
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [containerRef, contentRef, updateScale]);
  useEffect5(() => {
    if (resetOnChange) {
      setScale(maxScale);
      requestAnimationFrame(() => {
        updateScale();
      });
    } else {
      updateScale();
    }
  }, dependencies);
  return scale;
};
var useScale_default = useScale;

// src/useScrollIntoView.ts
import { useEffect as useEffect6, useRef } from "react";
var useScrollIntoView = (shouldScroll, options = { behavior: "smooth", block: "center" }, delay = 0) => {
  const elementRef = useRef(null);
  useEffect6(() => {
    if (!shouldScroll || !elementRef.current) return;
    const timeout = setTimeout(() => {
      elementRef.current?.scrollIntoView(options);
    }, delay);
    return () => clearTimeout(timeout);
  }, [shouldScroll, delay]);
  return elementRef;
};
var useScrollIntoView_default = useScrollIntoView;

// src/useResizeObserver.ts
import { useEffect as useEffect7 } from "react";
var useResizeObserver = (element, options, observerCallback) => {
  useEffect7(() => {
    if (!element || !("ResizeObserver" in window)) {
      return void 0;
    }
    const observer = new ResizeObserver(observerCallback);
    observer.observe(element, options);
    return () => {
      observer.disconnect();
    };
  }, [element, options, observerCallback]);
};
var useResizeObserver_default = useResizeObserver;

// src/useInfiniteScroll.ts
import { useEffect as useEffect8, useRef as useRef2, useCallback as useCallback4 } from "react";
var useInfiniteScroll = (callback, options = {}) => {
  const { threshold = 200, hasMore = true, isLoading = false } = options;
  const sentinelRef = useRef2(null);
  const handleObserver = useCallback4(
    (entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting && !isLoading && hasMore) {
        callback();
      }
    },
    [callback, isLoading, hasMore]
  );
  useEffect8(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: `0px 0px ${threshold}px 0px`
    });
    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }
    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [handleObserver, threshold]);
  return { sentinelRef };
};
var useInfiniteScroll_default = useInfiniteScroll;

// src/useQueryParam.ts
import { useCallback as useCallback5, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { QUERY_KEY } from "@shared/constants";
import { buildQueryUrl, createSearchQuery } from "@shared/utils";
var useQueryParam = (paramKey = QUERY_KEY.SEARCH) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const setQueryParam = useCallback5(
    (value) => {
      startTransition(() => {
        const queryString = createSearchQuery(searchParams, paramKey, value);
        router.push(buildQueryUrl(pathname, queryString));
      });
    },
    [pathname, router, searchParams, paramKey]
  );
  const removeQueryParam = useCallback5(() => {
    startTransition(() => {
      const params = new URLSearchParams(searchParams);
      params.delete(paramKey);
      const queryString = params.toString();
      router.push(buildQueryUrl(pathname, queryString));
    });
  }, [pathname, router, searchParams, paramKey]);
  return {
    isPending,
    setQueryParam,
    removeQueryParam
  };
};
var useQueryParam_default = useQueryParam;

// src/useDropdownPosition.ts
import { useState as useState4, useCallback as useCallback6, useEffect as useEffect9, useRef as useRef3 } from "react";
var DROPDOWN_GAP = 8;
var useDropdownPosition = ({
  isOpen,
  dropdownHeight = 250
}) => {
  const [coords, setCoords] = useState4(null);
  const [maxHeight, setMaxHeight] = useState4(dropdownHeight);
  const wrapperRef = useRef3(null);
  const rafRef = useRef3(null);
  const dropdownHeightRef = useRef3(dropdownHeight);
  useEffect9(() => {
    dropdownHeightRef.current = dropdownHeight;
  }, [dropdownHeight]);
  const checkPosition = useCallback6(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const { left, width } = rect;
    const height = dropdownHeightRef.current;
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    const shouldFlipAbove = spaceBelow < height && spaceAbove > spaceBelow;
    if (shouldFlipAbove) {
      setMaxHeight(Math.min(height, Math.max(0, spaceAbove)));
      setCoords({
        bottom: window.innerHeight - rect.top + DROPDOWN_GAP,
        left,
        width
      });
    } else {
      setMaxHeight(Math.min(height, Math.max(0, spaceBelow)));
      setCoords({ top: rect.bottom + DROPDOWN_GAP, left, width });
    }
  }, []);
  const cancelRaf = useCallback6(() => {
    if (rafRef.current === null) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);
  const scheduleCheck = useCallback6(() => {
    cancelRaf();
    rafRef.current = requestAnimationFrame(() => {
      checkPosition();
      rafRef.current = null;
    });
  }, [cancelRaf, checkPosition]);
  useEffect9(() => {
    if (isOpen) {
      scheduleCheck();
      return cancelRaf;
    }
    cancelRaf();
    setCoords(null);
  }, [isOpen, scheduleCheck, cancelRaf]);
  useEffect9(() => {
    if (isOpen) scheduleCheck();
  }, [dropdownHeight]);
  useEffect9(() => {
    if (!isOpen) return;
    const handleChange = () => scheduleCheck();
    window.addEventListener("resize", handleChange);
    window.addEventListener("scroll", handleChange, true);
    return () => {
      window.removeEventListener("resize", handleChange);
      window.removeEventListener("scroll", handleChange, true);
    };
  }, [isOpen, scheduleCheck]);
  useEffect9(() => {
    if (!isOpen) return;
    const observer = new ResizeObserver(() => scheduleCheck());
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [isOpen, scheduleCheck]);
  useEffect9(() => cancelRaf, [cancelRaf]);
  return { coords, maxHeight, wrapperRef };
};
var useDropdownPosition_default = useDropdownPosition;

// src/useOverlay.ts
import { useState as useState5 } from "react";
var useOverlay = () => {
  const [isOverlayVisible, setOverlayVisible] = useState5(false);
  const showOverlay = () => setOverlayVisible(true);
  const hideOverlay = () => setOverlayVisible(false);
  return { isOverlayVisible, showOverlay, hideOverlay };
};
var useOverlay_default = useOverlay;

// src/useHashScroll.ts
import { useEffect as useEffect10, useRef as useRef4 } from "react";
import { usePathname as usePathname2 } from "next/navigation";
import { SCROLL_CONFIG } from "@shared/constants";
var useHashScroll = ({
  behavior = SCROLL_CONFIG.BEHAVIOR,
  block = SCROLL_CONFIG.BLOCK,
  inline = SCROLL_CONFIG.INLINE,
  delay = SCROLL_CONFIG.DELAY,
  maxRetries = SCROLL_CONFIG.MAX_RETRIES,
  retryDelay = SCROLL_CONFIG.RETRY_DELAY,
  layoutStableDelay = SCROLL_CONFIG.LAYOUT_STABLE_DELAY
} = {}) => {
  const pathname = usePathname2();
  const retryCountRef = useRef4(0);
  const timeoutRef = useRef4(null);
  const resizeObserverRef = useRef4(null);
  useEffect10(() => {
    if (typeof window === "undefined") return;
    const scrollOptions = { behavior, block, inline };
    const clearCurrentTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    const disconnectObserver = () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
    const cleanup = () => {
      clearCurrentTimeout();
      disconnectObserver();
      retryCountRef.current = 0;
    };
    const attemptScroll = () => {
      const hashId = window.location.hash.slice(1);
      if (!hashId) return;
      const targetElement = document.getElementById(hashId);
      if (targetElement) {
        targetElement.scrollIntoView(scrollOptions);
        disconnectObserver();
        return;
      }
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        timeoutRef.current = setTimeout(attemptScroll, retryDelay);
      }
    };
    const handleLayoutChange = () => {
      clearCurrentTimeout();
      retryCountRef.current = 0;
      timeoutRef.current = setTimeout(attemptScroll, layoutStableDelay);
    };
    const initializeScroll = () => {
      cleanup();
      timeoutRef.current = setTimeout(() => {
        retryCountRef.current = 0;
        if (typeof ResizeObserver === "undefined") {
          attemptScroll();
          return;
        }
        resizeObserverRef.current = new ResizeObserver(handleLayoutChange);
        resizeObserverRef.current.observe(document.documentElement);
        handleLayoutChange();
      }, delay);
    };
    initializeScroll();
    window.addEventListener("hashchange", initializeScroll);
    return () => {
      window.removeEventListener("hashchange", initializeScroll);
      cleanup();
    };
  }, [
    pathname,
    behavior,
    block,
    inline,
    delay,
    maxRetries,
    retryDelay,
    layoutStableDelay
  ]);
};
var useHashScroll_default = useHashScroll;

// src/useCopyToClipboard.ts
import { useCallback as useCallback7, useState as useState6 } from "react";
var useCopyToClipboard = () => {
  const [copiedText, setCopiedText] = useState6(null);
  const copy = useCallback7(async (text) => {
    if (!navigator?.clipboard) {
      return {
        success: false,
        error: "Clipboard API not supported"
      };
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      return {
        success: true
      };
    } catch (error) {
      setCopiedText(null);
      return {
        success: false,
        error: `Failed to copy text. ${error instanceof Error ? `Error: ${error.message}` : ""}`
      };
    }
  }, []);
  return [copiedText, copy];
};

// src/useResponsiveColumns.ts
import { useState as useState7, useEffect as useEffect11 } from "react";
import { BREAKPOINTS } from "@shared/constants";
var useResponsiveColumns = ({
  mobile = 1,
  tablet = 2,
  desktop = 3,
  mobileBreakpoint = BREAKPOINTS.SM,
  tabletBreakpoint = BREAKPOINTS.LG
} = {}) => {
  const [columnCount, setColumnCount] = useState7(desktop);
  useEffect11(() => {
    const updateColumnCount = () => {
      const width = window.innerWidth;
      if (width < mobileBreakpoint) {
        setColumnCount(mobile);
      } else if (width < tabletBreakpoint) {
        setColumnCount(tablet);
      } else {
        setColumnCount(desktop);
      }
    };
    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);
    return () => window.removeEventListener("resize", updateColumnCount);
  }, [mobile, tablet, desktop, mobileBreakpoint, tabletBreakpoint]);
  return columnCount;
};

// src/useTruncation.ts
import { useRef as useRef5, useState as useState8, useEffect as useEffect12 } from "react";
var useTruncation = (content) => {
  const [isTruncated, setIsTruncated] = useState8(false);
  const elementRef = useRef5(null);
  useEffect12(() => {
    const checkTruncation = () => {
      if (elementRef.current) {
        const element = elementRef.current;
        const isHorizontallyTruncated = element.scrollWidth > element.clientWidth;
        const isVerticallyTruncated = element.scrollHeight > element.clientHeight;
        const computedStyle = window.getComputedStyle(element);
        const lineClamp = computedStyle.webkitLineClamp;
        let isLineClamped = false;
        if (lineClamp && lineClamp !== "none") {
          const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
          const maxHeight = lineHeight * parseInt(lineClamp, 10);
          isLineClamped = element.scrollHeight > maxHeight;
        }
        setIsTruncated(
          isHorizontallyTruncated || isVerticallyTruncated || isLineClamped
        );
      }
    };
    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);
    let resizeObserver = null;
    if (elementRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        checkTruncation();
      });
      resizeObserver.observe(elementRef.current);
    }
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkTruncation);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [content]);
  return {
    elementRef,
    isTruncated
  };
};

// src/useWindowWidth.ts
import { useEffect as useEffect13, useState as useState9 } from "react";
var useWindowWidth = () => {
  const [width, setWidth] = useState9(0);
  useEffect13(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
};

// src/usePersistentRef.ts
import { useRef as useRef6 } from "react";
var usePersistentRef = (initialValue, onChange) => {
  const ref = useRef6(initialValue);
  ref.current = new Proxy(ref.current, {
    set(target, prop, value) {
      const changed = Reflect.set(target, prop, value);
      onChange(ref.current);
      return changed;
    }
  });
  return ref;
};
export {
  useBreakpoint_default as useBreakpoint,
  useClickOutside_default as useClickOutside,
  useCopyToClipboard,
  useDebounce_default as useDebounce,
  useDropdownPosition_default as useDropdownPosition,
  useHashScroll_default as useHashScroll,
  useInfiniteScroll_default as useInfiniteScroll,
  useOverlay_default as useOverlay,
  usePersistentRef,
  useQueryParam_default as useQueryParam,
  useResizeObserver_default as useResizeObserver,
  useResponsiveColumns,
  useScale_default as useScale,
  useScrollIntoView_default as useScrollIntoView,
  useScrollSuggestionIntoView_default as useScrollSuggestionIntoView,
  useTruncation,
  useWindowWidth
};
